import React, { useState, useEffect, useRef, useContext } from 'react';
import { Play, Pause, Rewind, FastForward, Menu } from 'lucide-react';
import SettingsPanel from './SettingsPanel';
import { signOut } from 'firebase/auth';
import { analytics, auth } from './firebaseConfig';
import { logEvent } from 'firebase/analytics';
import { saveTrackPosition, markTrackCompleted, getUserProgress, clearUserProgress } from './firestore';
import type { UserProgressMap, AppearanceMode } from './firestore';
import { AuthContext } from './AuthContext';
import { resolveTrackUrl } from './storage';

interface Track {
  title: string;
  path: string;
  step: number | string;
}

interface LanguageLearningPlayerProps {
  tracks: Track[];
  artworkUrl: string;
  darkMode: boolean;
  appearance: AppearanceMode;
  onAppearanceChange: (a: AppearanceMode) => void;
  isSubscriber: boolean;
}

const SAVE_INTERVAL = 30000; // Save every 30 seconds

const makeMediaMetadata = (title: string) => new MediaMetadata({
  title,
  artist: 'SPANISH in 10 minutes a day® AUDIO',
  album: 'SPANISH in 10 minutes a day® AUDIO',
  artwork: [
    { src: `${window.location.origin}/logo192.png`, sizes: '192x192', type: 'image/png' },
    { src: `${window.location.origin}/logo512.png`, sizes: '512x512', type: 'image/png' },
  ],
});

const LanguageLearningPlayer: React.FC<LanguageLearningPlayerProps> = ({ tracks, artworkUrl, darkMode, appearance, onAppearanceChange, isSubscriber }) => {
  const [currentTrack, setCurrentTrack] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isSpeedMenuOpen, setIsSpeedMenuOpen] = useState(false);
  const [userProgress, setUserProgress] = useState<UserProgressMap | null>(null);
  const [isProgressLoaded, setIsProgressLoaded] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const speedMenuRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement>(new Audio());
  const animationRef = useRef<number>();
  const lastSaveTimeRef = useRef<number>(0);
  const userProgressRef = useRef<UserProgressMap | null>(null);
  const resolvedUrlsRef = useRef<{ [path: string]: string }>({});
  const isPlayingRef = useRef(false);
  const srcLoadedRef = useRef(false);
  const sleepTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sleepTimerTargetRef = useRef<number | null>(null);
  const isAutoAdvancingRef = useRef(false); // set in onended so cleanup doesn't kill mid-transition audio
  const { user } = useContext(AuthContext);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [sleepTimerMinutes, setSleepTimerMinutes] = useState<number | null>(null);


  useEffect(() => {
    const loadUserProgress = async () => {
      if (user) {
        const progress = await getUserProgress(user.uid);
        setUserProgress(progress);
        userProgressRef.current = progress;

        if (progress && Object.keys(progress).length > 0) {
          const mostRecentTrack = Object.entries(progress).reduce((latest, current) => {
            const [, data] = current;
            if (!latest || new Date(data.lastUpdated) > new Date(latest[1].lastUpdated)) {
              return current;
            }
            return latest;
          });

          if (mostRecentTrack) {
            const [step, data] = mostRecentTrack;
            const trackIndex = tracks.findIndex(track => track.step.toString() === step);
            if (trackIndex !== -1) {
              setCurrentTrack(trackIndex);
              setProgress(data.position);
            }
          }
        }
        setIsProgressLoaded(true);
      }
    };

    loadUserProgress();
  }, [user, tracks]);


  useEffect(() => {
    if (!isProgressLoaded) return;

    const audio = audioRef.current;
    srcLoadedRef.current = false;

    (async () => {
      // Check if onended already loaded this track directly (bypassing React render cycle)
      const wasAutoAdvancing = isAutoAdvancingRef.current;
      isAutoAdvancingRef.current = false;

      // Resolve authenticated download URL (fetched once per track, then cached)
      const path = tracks[currentTrack].path;
      if (!resolvedUrlsRef.current[path]) {
        resolvedUrlsRef.current[path] = await resolveTrackUrl(path);
      }

      if (wasAutoAdvancing) {
        // Audio is already loaded and playing — just update srcLoadedRef and duration
        srcLoadedRef.current = true;
        if (audio.duration > 0) setDuration(audio.duration);
        return;
      }

      audio.src = resolvedUrlsRef.current[path];
      audio.load();
      srcLoadedRef.current = true;
      // If play was requested while the URL was resolving, trigger it now
      if (isPlayingRef.current) {
        audio.play().catch(error => console.error("Playback failed:", error));
      }
    })();

    // Sync UI state for external pause (phone call, headphone disconnect, Siri)
    audio.onpause = () => {
      if ('mediaSession' in navigator) navigator.mediaSession.playbackState = 'paused';
      if (isPlayingRef.current) {
        setIsPlaying(false);
        isPlayingRef.current = false;
      }
    };

    // Sync UI state when browser or OS resumes audio without going through our handler
    audio.onplay = () => {
      if ('mediaSession' in navigator) navigator.mediaSession.playbackState = 'playing';
      if (!isPlayingRef.current) {
        isPlayingRef.current = true;
        setIsPlaying(true);
      }
    };

    audio.onloadedmetadata = () => {
      setDuration(audio.duration);
      audio.playbackRate = playbackRate;
      const savedProgress = userProgressRef.current;
      if (savedProgress && savedProgress[tracks[currentTrack].step.toString()]) {
        const savedPosition = savedProgress[tracks[currentTrack].step.toString()].position;
        audio.currentTime = savedPosition;
        setProgress(savedPosition);
      }
      setIsInitialLoad(false);
    };

    // Register this page as a media player with the OS (enables lock-screen controls)
    if ('mediaSession' in navigator) {
      navigator.mediaSession.metadata = makeMediaMetadata(tracks[currentTrack].title);

      // Call audio directly — don't rely on React state update being fast enough for iOS
      navigator.mediaSession.setActionHandler('play', () => {
        isPlayingRef.current = true;
        audioRef.current.play().catch(console.error);
        navigator.mediaSession.playbackState = 'playing';
        setIsPlaying(true);
      });
      navigator.mediaSession.setActionHandler('pause', () => {
        isPlayingRef.current = false;
        audioRef.current.pause();
        navigator.mediaSession.playbackState = 'paused';
        setIsPlaying(false);
      });
      navigator.mediaSession.setActionHandler('nexttrack', () => {
        if (currentTrack < tracks.length - 1) {
          saveProgress();
          lastSaveTimeRef.current = Date.now();
          isPlayingRef.current = true;
          setCurrentTrack(prev => prev + 1);
          setIsPlaying(true);
          setProgress(0);
        }
      });
      navigator.mediaSession.setActionHandler('previoustrack', () => {
        saveProgress();
        lastSaveTimeRef.current = Date.now();
        isPlayingRef.current = true;
        setCurrentTrack(prev => Math.max(0, prev - 1));
        setIsPlaying(true);
        setProgress(0);
      });
      // Use audioRef directly — skip() closes over stale `duration` state
      navigator.mediaSession.setActionHandler('seekbackward', (details) => {
        const a = audioRef.current;
        a.currentTime = Math.max(0, a.currentTime - (details.seekOffset ?? 15));
        setProgress(a.currentTime);
      });
      navigator.mediaSession.setActionHandler('seekforward', (details) => {
        const a = audioRef.current;
        a.currentTime = Math.min(a.duration, a.currentTime + (details.seekOffset ?? 15));
        setProgress(a.currentTime);
      });
    }

    // Pre-fetch next track URL so onended can load it synchronously
    if (currentTrack < tracks.length - 1) {
      const nextPath = tracks[currentTrack + 1].path;
      if (!resolvedUrlsRef.current[nextPath]) {
        resolveTrackUrl(nextPath).then(url => {
          resolvedUrlsRef.current[nextPath] = url;
        }).catch(() => {}); // silent — will retry in onended if needed
      }
    }

    // Log event when a new track starts playing
    logEvent(analytics, 'track_start', {
      track_title: tracks[currentTrack].title,
      track_step: tracks[currentTrack].step.toString()
    });

    audio.onended = () => {
      // Log event when the track finishes playing
      logEvent(analytics, 'track_complete', {
        track_title: tracks[currentTrack].title,
        track_step: tracks[currentTrack].step.toString()
      });

      const stepKey = tracks[currentTrack].step.toString();
      const trackData = userProgressRef.current?.[stepKey];

      // saveProgress() may have already marked this complete (if the 30s interval or a
      // pause fired within the last 10 seconds). Only increment playCount once.
      if (!trackData?.completed) {
        const currentPlayCount = trackData?.playCount ?? 0;
        if (user) {
          markTrackCompleted(user.uid, tracks[currentTrack].step, currentPlayCount);
        }
        const updatedProgress: UserProgressMap = {
          ...(userProgressRef.current || {}),
          [stepKey]: {
            position: 0,
            percent: 100,
            completed: true,
            playCount: currentPlayCount + 1,
            lastUpdated: new Date().toISOString(),
          },
        };
        userProgressRef.current = updatedProgress;
        setUserProgress(updatedProgress);
      }

      if (currentTrack < tracks.length - 1) {
        const nextIndex = currentTrack + 1;
        const nextPath = tracks[nextIndex].path;

        // Keep media session alive while we transition
        if ('mediaSession' in navigator) navigator.mediaSession.playbackState = 'playing';

        // Load and play next track DIRECTLY — don't wait for React's render cycle.
        // React renders are throttled when the screen is locked or app is backgrounded,
        // so relying on setCurrentTrack → useEffect would silently drop the advance.
        isAutoAdvancingRef.current = true;
        const loadNext = async () => {
          try {
            if (!resolvedUrlsRef.current[nextPath]) {
              resolvedUrlsRef.current[nextPath] = await resolveTrackUrl(nextPath);
            }
            // Clear old onloadedmetadata before load so it doesn't restore old track's position
            audio.onloadedmetadata = null;
            audio.src = resolvedUrlsRef.current[nextPath];
            audio.load();
            audio.playbackRate = playbackRate;

            // Restore saved position for the next track (if any)
            audio.addEventListener('loadedmetadata', () => {
              setDuration(audio.duration);
              setIsInitialLoad(false);
              const nextStepKey = tracks[nextIndex].step.toString();
              const savedPos = userProgressRef.current?.[nextStepKey]?.position ?? 0;
              if (savedPos > 10) {
                audio.currentTime = savedPos;
                setProgress(savedPos);
              }
            }, { once: true });

            await audio.play();

            // Update Media Session for new track
            if ('mediaSession' in navigator) {
              navigator.mediaSession.metadata = makeMediaMetadata(tracks[nextIndex].title);
              navigator.mediaSession.playbackState = 'playing';
            }
          } catch (e) {
            console.error('Auto-advance failed:', e);
            isAutoAdvancingRef.current = false;
          }
        };
        loadNext();

        // Update React state for the UI (may be deferred when locked — that's OK)
        setCurrentTrack(nextIndex);
        setProgress(0);
      } else {
        // Last track finished
        isPlayingRef.current = false;
        setIsPlaying(false);
        setCurrentTrack(0);
        setProgress(0);
        if ('mediaSession' in navigator) navigator.mediaSession.playbackState = 'paused';
      }
    };

    return () => {
      audio.onpause = null;
      audio.onplay = null;
      // If onended already loaded the next track directly, don't pause or clear src —
      // the audio is already playing the new track.
      if (!isAutoAdvancingRef.current) {
        audio.pause();
        audio.src = '';
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTrack, tracks, isProgressLoaded]);



  useEffect(() => {
    isPlayingRef.current = isPlaying;
    const audio = audioRef.current;
    if (isPlaying) {
      // Only call play() if src is already loaded; otherwise the async IIFE will call it
      if (srcLoadedRef.current) {
        audio.play().catch(error => console.error("Playback failed:", error));
      }
      animationRef.current = requestAnimationFrame(updateProgress);
    } else {
      audio.pause();
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      // Save progress when pausing
      saveProgress();
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying, currentTrack]);

  useEffect(() => {
    return () => {
      if (isPlayingRef.current) {
        saveProgress();
      }
    };
  }, []);


  useEffect(() => {
    audioRef.current.playbackRate = playbackRate;
  }, [playbackRate]);

  useEffect(() => {
    if ('mediaSession' in navigator) {
      navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
    }
  }, [isPlaying]);

  // Sleep timer — pauses audio after the selected interval
  useEffect(() => {
    if (sleepTimerRef.current) clearTimeout(sleepTimerRef.current);
    if (sleepTimerMinutes !== null) {
      sleepTimerTargetRef.current = Date.now() + sleepTimerMinutes * 60 * 1000;
      sleepTimerRef.current = setTimeout(() => {
        setIsPlaying(false);
        isPlayingRef.current = false;
        setSleepTimerMinutes(null);
        sleepTimerTargetRef.current = null;
      }, sleepTimerMinutes * 60 * 1000);
    } else {
      sleepTimerTargetRef.current = null;
    }
    return () => {
      if (sleepTimerRef.current) clearTimeout(sleepTimerRef.current);
    };
  }, [sleepTimerMinutes]);

  // Save progress on an interval so saves work while the screen is locked
  // (requestAnimationFrame is suspended when locked; setInterval continues for active media players)
  useEffect(() => {
    if (saveIntervalRef.current) clearInterval(saveIntervalRef.current);
    if (isPlaying) {
      saveIntervalRef.current = setInterval(() => {
        saveProgress();
      }, SAVE_INTERVAL);
    }
    return () => {
      if (saveIntervalRef.current) {
        clearInterval(saveIntervalRef.current);
        saveIntervalRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, currentTrack]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (speedMenuRef.current && !speedMenuRef.current.contains(event.target as Node)) {
        setIsSpeedMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);


  const updateProgress = () => {
    const audio = audioRef.current;
    const audioDuration = audio.duration;
    const currentTime = audioDuration ? Math.min(audio.currentTime, audioDuration) : audio.currentTime;
    setProgress(currentTime);

    // Update OS lock-screen scrubber position
    if ('mediaSession' in navigator && navigator.mediaSession.setPositionState && audioDuration > 0) {
      try {
        navigator.mediaSession.setPositionState({
          duration: audio.duration,
          position: Math.min(currentTime, audio.duration),
          playbackRate: audio.playbackRate,
        });
      } catch (_) {
        // setPositionState can throw if duration is not yet valid
      }
    }

    // Wall-clock sleep timer fallback (fires on next rAF tick after target)
    if (sleepTimerTargetRef.current !== null && Date.now() >= sleepTimerTargetRef.current) {
      setIsPlaying(false);
      isPlayingRef.current = false;
      setSleepTimerMinutes(null);
      sleepTimerTargetRef.current = null;
    }

    animationRef.current = requestAnimationFrame(updateProgress);
  };


  const togglePlayPause = (index: number) => {
    if (currentTrack !== index) {
      saveProgress();
      lastSaveTimeRef.current = Date.now(); // prevent timer from firing immediately after switch
      setCurrentTrack(index);
      setIsPlaying(true);
      setProgress(0);
    } else {
      setIsPlaying(!isPlaying);
    }
  };

  const skip = (seconds: number) => {
    const newTime = audioRef.current.currentTime + seconds;
    audioRef.current.currentTime = Math.max(0, Math.min(newTime, duration));
    setProgress(audioRef.current.currentTime);
  };



  const speeds = [0.6, 0.75, 1, 1.25, 1.5];

  const toggleSpeedMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsSpeedMenuOpen(!isSpeedMenuOpen);
  };

  const handleSpeedChange = (speed: number) => {
    setPlaybackRate(speed);
    setIsSpeedMenuOpen(false);
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getTrackStyle = (index: number): React.CSSProperties => {
    const baseColor = darkMode ? '#3f3f46' : '#e7e5e4';

    // Currently playing — solid orange
    if (currentTrack === index && isPlaying) {
      return { backgroundColor: '#f78320' };
    }

    const stepKey = tracks[index].step.toString();
    const trackData = userProgressRef.current?.[stepKey];

    // Completed — full 40% orange wash
    if (trackData?.completed) {
      return {
        backgroundColor: baseColor,
        backgroundImage: 'linear-gradient(rgba(247,131,32,0.40), rgba(247,131,32,0.40))',
      };
    }

    // Partial listen — gradient wash fading at pause point
    const pct = trackData?.percent ?? 0;
    if (pct > 2) {
      return {
        backgroundColor: baseColor,
        backgroundImage: `linear-gradient(to right,
          rgba(247,131,32,0.40) 0%,
          rgba(247,131,32,0.40) ${pct}%,
          rgba(247,131,32,0.10) ${pct + 10}%,
          rgba(247,131,32,0.00) ${pct + 21}%)`,
      };
    }

    // Unplayed
    return { backgroundColor: baseColor };
  };

  const saveProgress = () => {
    if (!user || isInitialLoad) return;

    const audio = audioRef.current;
    const position = audio.currentTime;
    const audioDuration = audio.duration;

    // Don't save while audio metadata isn't loaded — currentTime would be 0
    // and would trigger the micro-skip, corrupting the saved position.
    if (!audioDuration || isNaN(audioDuration)) return;
    const stepKey = tracks[currentTrack].step.toString();
    const trackData = userProgressRef.current?.[stepKey];

    // Completion: within last 10 seconds of audio
    if (audioDuration > 0 && (audioDuration - position) <= 10) {
      const currentPlayCount = trackData?.playCount ?? 0;
      markTrackCompleted(user.uid, tracks[currentTrack].step, currentPlayCount);
      const updatedProgress: UserProgressMap = {
        ...(userProgressRef.current || {}),
        [stepKey]: {
          position: 0,
          percent: 100,
          completed: true,
          playCount: currentPlayCount + 1,
          lastUpdated: new Date().toISOString(),
        },
      };
      userProgressRef.current = updatedProgress;
      setUserProgress(updatedProgress);
      return;
    }

    // Micro-skip: < 10 seconds — reset to unplayed position (keep completed/playCount unchanged)
    if (position < 10) {
      saveTrackPosition(user.uid, tracks[currentTrack].step, 0, 0);
      const updatedProgress: UserProgressMap = {
        ...(userProgressRef.current || {}),
        [stepKey]: {
          ...(trackData || { completed: false, playCount: 0 }),
          position: 0,
          percent: 0,
          lastUpdated: new Date().toISOString(),
        },
      };
      userProgressRef.current = updatedProgress;
      setUserProgress(updatedProgress);
      return;
    }

    // Normal save
    const pct = audioDuration > 0 ? (position / audioDuration) * 100 : 0;
    saveTrackPosition(user.uid, tracks[currentTrack].step, position, pct);
    const updatedProgress: UserProgressMap = {
      ...(userProgressRef.current || {}),
      [stepKey]: {
        ...(trackData || { completed: false, playCount: 0 }),
        position,
        percent: pct,
        lastUpdated: new Date().toISOString(),
      },
    };
    userProgressRef.current = updatedProgress;
    setUserProgress(updatedProgress);
  };

  return (
    <div className={`max-w-2xl mx-auto ${darkMode ? 'text-white' : 'text-black'}`}>

      {/* Artwork — full-width header banner, sticky on scroll */}
      <div className="artwork-header sticky top-0 z-10 relative">
        <img
          src={artworkUrl}
          alt="SPANISH in 10 minutes a day AUDIO"
          className="w-full h-auto shadow-md block"
        />
        {/* Hamburger menu button — stays on screen with sticky artwork */}
        <button
          onClick={() => setIsMenuOpen(true)}
          className="absolute top-3 right-3 p-2 rounded-full bg-black/30 text-white backdrop-blur-sm hover:bg-black/50 transition-colors"
          aria-label="Open menu"
        >
          <Menu size={20} />
        </button>
        {/* Soft gradient fade */}
        <div
          className="absolute -bottom-6 left-0 right-0 h-6 pointer-events-none"
          style={{
            background: `linear-gradient(to bottom, ${darkMode ? '#111827' : '#ffffff'} 0%, transparent 100%)`,
          }}
        />
      </div>

      {/* Tracks */}
      <div className="px-3 pt-6 pb-4">
      {tracks.map((track, index) => (
        <div
          key={index}
          style={getTrackStyle(index)}
          className={`mb-4 p-4 rounded-lg relative
            ${currentTrack === index && !isPlaying ? 'ring-2 ring-amber-600/60' : ''}
            ${currentTrack === index
              ? (isPlaying ? 'text-white' : (darkMode ? 'text-stone-200' : 'text-stone-600'))
              : (darkMode ? 'text-stone-300' : 'text-stone-500')
            }`}
          onClick={() => togglePlayPause(index)}
        >
          <div className="flex justify-between items-start">
            <div>
              <div className="text-sm">Step {track.step}</div>
              <div className="text-2xl">{track.title}</div>
            </div>
            {currentTrack === index && (
              <div className="absolute top-1 right-3 flex items-center space-x-2 rounded-lg p-1">

                <div className="relative text-sm" ref={speedMenuRef}>
                  <button
                    onClick={toggleSpeedMenu}
                    className={`flex items-center space-x-1 bg-transparent ${isPlaying || darkMode ? 'text-white' : 'text-stone-600'}`}
                    aria-label="Playback speed"
                  >
                    <span>{playbackRate}x</span>
                    {/* <ChevronDown size={14} /> */}
                  </button>
                  {isSpeedMenuOpen && (
                    <div className="absolute top-full left-0 mt-1 bg-stone-700 rounded shadow-xl z-10">
                      {speeds.map((speed) => (
                        <button
                          key={speed}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSpeedChange(speed);
                          }}
                          className="block w-full text-left px-4 py-2 hover:bg-stone-600 text-white">
                          {speed}x
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <button onClick={(e) => { e.stopPropagation(); skip(-15); }} className="p-2" aria-label="Rewind 15 seconds">
                  <Rewind size={22} />
                </button>

                <button onClick={(e) => { e.stopPropagation(); togglePlayPause(index); }} className="p-2 rounded-full" aria-label={isPlaying ? "Pause" : "Play"}>
                  {isPlaying ? <Pause size={26} color="white" /> : <Play size={26} color={darkMode ? 'white' : '#57534e'} />}
                </button>

                <button onClick={(e) => { e.stopPropagation(); skip(15); }} className="p-2" aria-label="Fast forward 15 seconds">
                  <FastForward size={22} />
                </button>

              </div>
            )}
          </div>
          {currentTrack === index && (
            <div className="mt-2">
              <div className="flex justify-between text-sm">
                <span>{formatTime(progress)}</span>
                <span>{formatTime(duration)}</span>
              </div>
              <div className={`h-2 w-full rounded-full mt-1 ${isPlaying ? 'bg-stone-200' : (darkMode ? 'bg-stone-200' : 'bg-stone-400')}`}>
                <div
                  className={`h-2 ${isPlaying ? 'bg-amber-700' : 'bg-zinc-600'} rounded-full`}
                  style={{ width: `${Math.min((progress / duration) * 100, 100)}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>
      ))}
      </div>

      {/* Menu Panel */}
      <SettingsPanel
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        darkMode={darkMode}
        appearance={appearance}
        onAppearanceChange={onAppearanceChange}
        isSubscriber={isSubscriber}
        userId={user?.uid ?? ''}
        userEmail={user?.email ?? ''}
        sleepTimerMinutes={sleepTimerMinutes}
        onSleepTimerChange={setSleepTimerMinutes}
        userProgress={userProgress}
        tracks={tracks}
        onClearProgress={() => {
          if (user) {
            clearUserProgress(user.uid).then(() => {
              userProgressRef.current = null;
              setUserProgress(null);
              setCurrentTrack(0);
              setProgress(0);
            });
          }
        }}
        onLogOut={() => {
          signOut(auth).catch(error => console.error('Error logging out:', error));
        }}
      />


    </div>
  );
};

export default LanguageLearningPlayer;
